import React, { useState, useEffect, useRef } from 'react';
import { T, Th, Tr, ActionBtn, Modal, TableCard, SearchInput, Toast, PrimaryBtn } from './theme';
import { listBooks, createBook, updateBook, deleteBook, getUploadUrl, uploadImageToS3 } from '../services/bookService';

const CATEGORIES = ['Agama', 'Vaikhanasa', 'Sanskrit', 'Philosophy', 'Ritual', 'History', 'Other'];
const LANGUAGES = ['English', 'Sanskrit', 'Telugu', 'Tamil', 'Hindi', 'Kannada'];
const PER_PAGE = 15;

const EMPTY_FORM = {
  title: '', subtitle: '', author: '', publisher: '', isbn: '',
  language: 'English', category: 'Agama', publicationDate: '', edition: '',
  pages: '', description: '', coverImageUrl: '', activeFlag: true,
};

const Label = ({ children }) => <label className="vrv-label">{children}</label>;
const Field = ({ label, children }) => (
  <div><Label>{label}</Label>{children}</div>
);

const AdminBooksTab = () => {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editBook, setEditBook] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('All');
  const [page, setPage] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [previewUrl, setPreviewUrl] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const fileRef = useRef(null);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3500);
  };

  const fetchBooks = async () => {
    setLoading(true);
    try {
      const data = await listBooks();
      setBooks(data.books || []);
    } catch { showToast('Failed to load books', 'error'); }
    setLoading(false);
  };

  useEffect(() => { fetchBooks(); }, []); // eslint-disable-line
  useEffect(() => { setPage(1); }, [search, filterCat]);

  const openAdd = () => {
    setEditBook(null);
    setForm(EMPTY_FORM);
    setPreviewUrl('');
    setImageFile(null);
    setShowForm(true);
  };

  const openEdit = (book) => {
    setEditBook(book);
    setForm({
      title: book.title || '', subtitle: book.subtitle || '', author: book.author || '',
      publisher: book.publisher || '', isbn: book.isbn || '', language: book.language || 'English',
      category: book.category || 'Agama', publicationDate: book.publicationDate || '',
      edition: book.edition || '', pages: book.pages || '', description: book.description || '',
      coverImageUrl: book.coverImageUrl || '', activeFlag: book.activeFlag !== false,
    });
    setPreviewUrl(book.coverImageUrl || '');
    setImageFile(null);
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditBook(null); setImageFile(null); setPreviewUrl(''); };

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) { showToast('Only jpg, png, webp allowed', 'error'); return; }
    if (file.size > 5 * 1024 * 1024) { showToast('Image must be under 5MB', 'error'); return; }
    setImageFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { showToast('Title is required', 'error'); return; }
    if (!form.author.trim()) { showToast('Author is required', 'error'); return; }
    setSaving(true);

    try {
      let coverImageUrl = form.coverImageUrl;

      // Upload image if new file selected
      if (imageFile) {
        setUploading(true);
        const ext = imageFile.name.split('.').pop();
        const fileName = `books/${(form.title || 'book').toLowerCase().replace(/\s+/g, '-')}-${Date.now()}.${ext}`;
        const { uploadUrl, imageUrl } = await getUploadUrl(fileName, imageFile.type);
        await uploadImageToS3(uploadUrl, imageFile);
        coverImageUrl = imageUrl;
        setUploading(false);
      }

      const payload = {
        ...form,
        coverImageUrl,
        pages: form.pages ? Number(form.pages) : undefined,
        createdBy: localStorage.getItem('vrvEmail') || 'admin',
        updatedBy: localStorage.getItem('vrvEmail') || 'admin',
      };

      if (editBook) {
        await updateBook(editBook.bookId, payload);
        showToast('Book updated successfully');
      } else {
        await createBook(payload);
        showToast('Book added successfully');
      }
      closeForm();
      fetchBooks();
    } catch {
      showToast('Failed to save book', 'error');
    }
    setSaving(false);
    setUploading(false);
  };

  const handleDelete = async () => {
    try {
      await deleteBook(deleteTarget.bookId);
      showToast('Book deleted');
      setDeleteTarget(null);
      fetchBooks();
    } catch { showToast('Delete failed', 'error'); }
  };

  const handleToggleActive = async (book) => {
    try {
      await updateBook(book.bookId, { activeFlag: !book.activeFlag, updatedBy: localStorage.getItem('vrvEmail') || 'admin' });
      showToast(`Book marked ${!book.activeFlag ? 'Active' : 'Inactive'}`);
      fetchBooks();
    } catch { showToast('Failed to update status', 'error'); }
  };

  const filtered = books.filter(b => {
    const matchCat = filterCat === 'All' || b.category === filterCat;
    const q = search.toLowerCase();
    const matchSearch = !q || [b.title, b.author, b.publisher, b.isbn, b.category]
      .some(v => String(v || '').toLowerCase().includes(q));
    return matchCat && matchSearch;
  });
  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const start = (page - 1) * PER_PAGE;
  const paginated = filtered.slice(start, start + PER_PAGE);

  const stats = [
    { label: 'Total', value: books.length },
    { label: 'Active', value: books.filter(b => b.activeFlag !== false).length },
    { label: 'Inactive', value: books.filter(b => b.activeFlag === false).length },
  ];

  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {stats.map(s => (
          <div key={s.label} className="vrv-card p-4 text-center" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
            <p className="text-2xl font-bold" style={{ fontFamily: 'Poppins, sans-serif', color: T.navy }}>{s.value}</p>
            <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: T.muted }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <SearchInput value={search} onChange={e => setSearch(e.target.value)} placeholder="Search books…" />
        <select value={filterCat} onChange={e => setFilterCat(e.target.value)}
          className="vrv-select" style={{ minWidth: '150px', minHeight: '44px' }}>
          <option value="All">All Categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <button onClick={openAdd} className="btn-navy whitespace-nowrap" style={{ minHeight: '44px', padding: '0 1.5rem' }}>
          + Add Book
        </button>
      </div>

      {/* Loading */}
      {loading && <div className="space-y-3">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="shimmer h-14 rounded-2xl" />)}</div>}

      {/* Table */}
      {!loading && books.length > 0 && (
        <>
          {/* Desktop */}
          <div className="desktop-table">
            <TableCard>
              <div className="overflow-x-auto">
                <table className="w-full text-sm vrv-table-sticky">
                  <thead>
                    <tr>
                      <Th>#</Th><Th>Cover</Th><Th>Title</Th><Th>Author</Th>
                      <Th>Category</Th><Th>Language</Th><Th>Status</Th><Th center>Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map((book, i) => (
                      <Tr key={book.bookId}>
                        <td className="px-4 py-3 text-xs" style={{ color: T.muted }}>{start + i + 1}</td>
                        <td className="px-4 py-3">
                          {book.coverImageUrl
                            ? <img src={book.coverImageUrl} alt={book.title} className="w-10 h-14 object-cover rounded-lg" style={{ border: '1px solid rgba(0,0,0,0.08)' }} />
                            : <div className="w-10 h-14 rounded-lg flex items-center justify-center text-lg" style={{ background: `linear-gradient(135deg, ${T.navy}, ${T.navyDark})` }}>📖</div>
                          }
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-semibold text-sm leading-tight" style={{ color: T.ink }}>{book.title}</p>
                          {book.subtitle && <p className="text-xs mt-0.5" style={{ color: T.muted }}>{book.subtitle}</p>}
                        </td>
                        <td className="px-4 py-3 text-xs" style={{ color: T.muted }}>{book.author || '—'}</td>
                        <td className="px-4 py-3">
                          {book.category && <span className="badge badge-gold" style={{ fontSize: '9px' }}>{book.category}</span>}
                        </td>
                        <td className="px-4 py-3 text-xs" style={{ color: T.muted }}>{book.language || '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`badge ${book.activeFlag !== false ? 'badge-success' : 'badge-muted'}`}>
                            {book.activeFlag !== false ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1.5 justify-center flex-wrap">
                            <ActionBtn onClick={() => openEdit(book)} color="gold" small>Edit</ActionBtn>
                            <ActionBtn onClick={() => handleToggleActive(book)} color={book.activeFlag !== false ? 'muted' : 'green'} small>
                              {book.activeFlag !== false ? 'Deactivate' : 'Activate'}
                            </ActionBtn>
                            <ActionBtn onClick={() => setDeleteTarget(book)} color="red" small>Delete</ActionBtn>
                          </div>
                        </td>
                      </Tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              <div className="flex items-center justify-between px-5 py-3" style={{ background: '#F1EDE4', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                <p className="text-xs" style={{ color: T.muted }}>{start + 1}–{Math.min(start + PER_PAGE, filtered.length)} of {filtered.length}</p>
                <div className="flex gap-1">
                  <button onClick={() => setPage(p => Math.max(p - 1, 1))} disabled={page === 1}
                    className="px-3 py-1.5 text-[11px] font-bold rounded-xl disabled:opacity-40"
                    style={{ border: '1px solid rgba(0,0,0,0.1)', color: T.muted }}>‹ Prev</button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    const pg = page <= 3 ? i + 1 : page + i - 2;
                    if (pg < 1 || pg > totalPages) return null;
                    return (
                      <button key={pg} onClick={() => setPage(pg)}
                        className="px-3 py-1.5 text-[11px] font-bold rounded-xl"
                        style={page === pg ? { background: T.navy, color: 'white', border: `1px solid ${T.navy}` } : { border: '1px solid rgba(0,0,0,0.1)', color: T.muted }}>
                        {pg}
                      </button>
                    );
                  })}
                  <button onClick={() => setPage(p => Math.min(p + 1, totalPages))} disabled={page === totalPages}
                    className="px-3 py-1.5 text-[11px] font-bold rounded-xl disabled:opacity-40"
                    style={{ border: '1px solid rgba(0,0,0,0.1)', color: T.muted }}>Next ›</button>
                </div>
              </div>
            </TableCard>
          </div>

          {/* Mobile Cards */}
          <div className="mobile-cards space-y-4">
            {paginated.map((book) => (
              <div key={book.bookId} className="vrv-card p-4" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
                <div className="flex gap-3 mb-3">
                  {book.coverImageUrl
                    ? <img src={book.coverImageUrl} alt={book.title} className="w-14 h-20 object-cover rounded-xl flex-shrink-0" style={{ border: '1px solid rgba(0,0,0,0.08)' }} />
                    : <div className="w-14 h-20 rounded-xl flex items-center justify-center text-2xl flex-shrink-0" style={{ background: `linear-gradient(135deg, ${T.navy}, ${T.navyDark})` }}>📖</div>
                  }
                  <div className="flex-1 min-w-0">
                    <p className="font-bold truncate" style={{ fontFamily: 'Poppins, sans-serif', color: T.navy }}>{book.title}</p>
                    {book.subtitle && <p className="text-xs truncate" style={{ color: T.muted }}>{book.subtitle}</p>}
                    <p className="text-xs mt-1" style={{ color: T.muted }}>by {book.author || '—'}</p>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {book.category && <span className="badge badge-gold" style={{ fontSize: '9px' }}>{book.category}</span>}
                      <span className={`badge ${book.activeFlag !== false ? 'badge-success' : 'badge-muted'}`} style={{ fontSize: '9px' }}>
                        {book.activeFlag !== false ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap pt-3" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                  <ActionBtn onClick={() => openEdit(book)} color="gold">Edit</ActionBtn>
                  <ActionBtn onClick={() => handleToggleActive(book)} color={book.activeFlag !== false ? 'muted' : 'green'}>
                    {book.activeFlag !== false ? 'Deactivate' : 'Activate'}
                  </ActionBtn>
                  <ActionBtn onClick={() => setDeleteTarget(book)} color="red">Delete</ActionBtn>
                </div>
              </div>
            ))}
            {/* Mobile Pagination */}
            <div className="flex items-center justify-center gap-2 pt-2">
              <button onClick={() => setPage(p => Math.max(p - 1, 1))} disabled={page === 1}
                className="btn-outline text-xs px-4 py-2" style={{ minHeight: '40px' }}>‹ Prev</button>
              <span className="text-xs" style={{ color: T.muted }}>Page {page} of {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(p + 1, totalPages))} disabled={page === totalPages}
                className="btn-outline text-xs px-4 py-2" style={{ minHeight: '40px' }}>Next ›</button>
            </div>
          </div>
        </>
      )}

      {!loading && books.length === 0 && (
        <div className="vrv-card text-center py-16">
          <p className="text-5xl mb-4">📚</p>
          <p className="font-bold text-lg mb-2" style={{ fontFamily: 'Poppins, sans-serif', color: T.navy }}>No Books Yet</p>
          <p className="text-sm mb-4" style={{ color: T.muted }}>Add the first book to the catalog.</p>
          <button onClick={openAdd} className="btn-navy">Add Book</button>
        </div>
      )}

      {/* Add / Edit Modal */}
      {showForm && (
        <Modal onClose={closeForm} title={editBook ? 'Edit Book' : 'Add New Book'} subtitle="Book Management" wide>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Field label="Title *">
                  <input name="title" value={form.title} onChange={handleChange} required
                    className="vrv-input" placeholder="Book title" />
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Subtitle">
                  <input name="subtitle" value={form.subtitle} onChange={handleChange}
                    className="vrv-input" placeholder="Optional subtitle" />
                </Field>
              </div>
              <Field label="Author *">
                <input name="author" value={form.author} onChange={handleChange} required
                  className="vrv-input" placeholder="Author name" />
              </Field>
              <Field label="Publisher">
                <input name="publisher" value={form.publisher} onChange={handleChange}
                  className="vrv-input" placeholder="Publisher name" />
              </Field>
              <Field label="ISBN">
                <input name="isbn" value={form.isbn} onChange={handleChange}
                  className="vrv-input" placeholder="e.g. 9781234567890" />
              </Field>
              <Field label="Language">
                <select name="language" value={form.language} onChange={handleChange} className="vrv-select">
                  {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </Field>
              <Field label="Category">
                <select name="category" value={form.category} onChange={handleChange} className="vrv-select">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Publication Date">
                <input name="publicationDate" type="date" value={form.publicationDate} onChange={handleChange}
                  className="vrv-input" />
              </Field>
              <Field label="Edition">
                <input name="edition" value={form.edition} onChange={handleChange}
                  className="vrv-input" placeholder="e.g. 2nd" />
              </Field>
              <Field label="Pages">
                <input name="pages" type="number" min="1" value={form.pages} onChange={handleChange}
                  className="vrv-input" placeholder="Number of pages" />
              </Field>
              <div className="md:col-span-2">
                <Field label="Description">
                  <textarea name="description" value={form.description} onChange={handleChange}
                    rows="4" className="vrv-input resize-none" placeholder="Book description…" />
                </Field>
              </div>

              {/* Cover Image Upload */}
              <div className="md:col-span-2">
                <Label>Cover Image</Label>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    {previewUrl
                      ? <img src={previewUrl} alt="Preview" className="w-24 h-32 object-cover rounded-xl" style={{ border: '2px solid rgba(212,175,55,0.3)' }} />
                      : <div className="w-24 h-32 rounded-xl flex items-center justify-center text-3xl" style={{ background: '#F1EDE4', border: '2px dashed rgba(0,0,0,0.12)' }}>📖</div>
                    }
                  </div>
                  <div className="flex-1">
                    <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
                      onChange={handleFileChange} className="hidden" />
                    <button type="button" onClick={() => fileRef.current?.click()}
                      className="btn-outline text-xs" style={{ minHeight: '40px', marginBottom: '0.5rem' }}>
                      {imageFile ? '📷 Change Image' : '📷 Upload Cover'}
                    </button>
                    <p className="text-xs" style={{ color: T.muted }}>JPG, PNG, WebP · Max 5MB</p>
                    {imageFile && (
                      <button type="button" onClick={() => { setImageFile(null); setPreviewUrl(editBook?.coverImageUrl || ''); }}
                        className="text-xs mt-1" style={{ color: T.danger }}>✕ Remove</button>
                    )}
                  </div>
                </div>
              </div>

              <div className="md:col-span-2 flex items-center gap-3">
                <input type="checkbox" id="activeFlag" name="activeFlag" checked={form.activeFlag}
                  onChange={handleChange} className="w-4 h-4 accent-gold" />
                <label htmlFor="activeFlag" className="text-sm font-medium cursor-pointer" style={{ color: T.ink }}>
                  Active (visible on public catalog)
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
              <ActionBtn onClick={closeForm} color="muted" type="button">Cancel</ActionBtn>
              <button type="submit" disabled={saving} className="btn-navy" style={{ opacity: saving ? 0.6 : 1 }}>
                {uploading ? 'Uploading…' : saving ? 'Saving…' : editBook ? 'Update Book →' : 'Add Book →'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete Confirm Modal */}
      {deleteTarget && (
        <Modal onClose={() => setDeleteTarget(null)} title="Delete Book" subtitle="Confirm Action">
          <p className="text-sm mb-2" style={{ color: T.muted }}>
            Are you sure you want to delete <strong style={{ color: T.ink }}>{deleteTarget.title}</strong>?
          </p>
          <p className="text-xs mb-6" style={{ color: T.muted }}>This action cannot be undone.</p>
          <div className="flex justify-end gap-3">
            <ActionBtn onClick={() => setDeleteTarget(null)} color="muted">Cancel</ActionBtn>
            <ActionBtn onClick={handleDelete} color="red">Yes, Delete</ActionBtn>
          </div>
        </Modal>
      )}

      <Toast toast={toast} onClose={() => setToast({ show: false, message: '', type: '' })} />
    </div>
  );
};

export default AdminBooksTab;
